/**
 * User management utilities for marketplace users
 */

import { getStorageItem, setStorageItem } from './storage';

const USERS_KEY = 'marketplace_users';

/**
 * Get all marketplace users
 * @returns {Array} Array of user objects
 */
export const getAllUsers = () => {
  return getStorageItem(USERS_KEY, []);
};

/**
 * Get user by ID
 * @param {string} userId - User ID
 * @returns {Object|null} User object or null
 */
export const getUserById = (userId) => {
  const users = getAllUsers();
  return users.find(user => user.id === userId) || null;
};

/**
 * Get user by email
 * @param {string} email - User email
 * @returns {Object|null} User object or null
 */
export const getUserByEmail = (email) => {
  const users = getAllUsers();
  return users.find(user => user.email.toLowerCase() === email.toLowerCase()) || null;
};

/**
 * Create a new marketplace user
 * @param {Object} userData - User data
 * @returns {Object} Created user
 */
export const createUser = (userData) => {
  const users = getAllUsers();

  const newUser = {
    id: `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    name: userData.name,
    email: userData.email,
    phone: userData.phone || '',
    user_type: userData.user_type, // 'farmer' or 'restaurant'
    business_name: userData.business_name || '',
    created_at: new Date().toISOString()
  };

  users.push(newUser);
  setStorageItem(USERS_KEY, users);

  return newUser;
};

/**
 * Update an existing user
 * @param {string} userId - User ID
 * @param {Object} updates - Fields to update
 * @returns {Object|null} Updated user or null
 */
export const updateUser = (userId, updates) => {
  const users = getAllUsers();
  const index = users.findIndex(user => user.id === userId);

  if (index === -1) return null;

  users[index] = { ...users[index], ...updates };
  setStorageItem(USERS_KEY, users);

  return users[index];
};

/**
 * Authenticate marketplace user
 * @param {string} email - User email
 * @param {string} password - User password (mock - accepts 'demo123')
 * @returns {Object|null} User object if authenticated, null otherwise
 */
export const authenticateMarketplaceUser = (email, password) => {
  // For demo purposes, accept 'demo123' as password
  if (password !== 'demo123') return null;

  return getUserByEmail(email);
};

/**
 * Get current logged-in marketplace user
 * @returns {Object|null} Current user or null
 */
export const getCurrentMarketplaceUser = () => {
  return getStorageItem('marketplace_user', null);
};

/**
 * Set current logged-in marketplace user
 * @param {Object} user - User object
 */
export const setCurrentMarketplaceUser = (user) => {
  setStorageItem('marketplace_user', user);
  setStorageItem('marketplace_auth', 'marketplace_token');
};

/**
 * Check if user is authenticated for marketplace
 * @returns {boolean}
 */
export const isMarketplaceAuthenticated = () => {
  return getStorageItem('marketplace_auth', null) !== null;
};

/**
 * Logout marketplace user
 */
export const logoutMarketplaceUser = () => {
  setStorageItem('marketplace_user', null);
  setStorageItem('marketplace_auth', null);
};

/**
 * Check if current user is a farmer
 * @returns {boolean}
 */
export const isFarmer = () => {
  const user = getCurrentMarketplaceUser();
  return user?.user_type === 'farmer';
};

/**
 * Check if current user is a restaurant
 * @returns {boolean}
 */
export const isRestaurant = () => {
  const user = getCurrentMarketplaceUser();
  return user?.user_type === 'restaurant';
};
